import React, { useState } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import {
  CaptionText,
  H3Text,
  FontScale,
  FontWeight,
} from '@prisjakt/ui/text/variants';
import PriceInformation from '../../price-information';
import { sortOffersByPrice } from 'src/components/price-list/sorting';
import PriceListHeader from './price-list-header';
import PriceListFooter from './price-list-footer';
import PriceListBody from './price-list-body';
import { IPriceListPanelProps } from './types';
import { getCommonListProps } from './utils';
import { getSplitOfferlist, getListLimits } from './utils/split-list';
import MissingShop from '../../missing-shop';
import OfferListEmptyMessage from '../offer-list-empty-message';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';
import { PricePanel, HeaderWrapper } from './styles';

export const PriceListPanel = (props: IPriceListPanelProps) => {
  const {
    me,
    filteredOffers,
    showProductInformationTexts,
    storeStatistics,
  } = props;
  const { t } = useAppContext();
  const { getActiveFilters, getPriceDisplay } = useOfferListFilterContext();
  const [showMorePrices, setShowMorePrices] = useState(false);
  const [showMoreInSecondaryList, setShowMoreInSecondaryList] = useState(false);

  const getOffersList = () => {
    const sortedOffers = sortOffersByPrice(getPriceDisplay(), filteredOffers);
    return getSplitOfferlist(sortedOffers, getActiveFilters());
  };

  const offersList = getOffersList();
  const commonListProps = getCommonListProps(props);
  const listLimits = getListLimits(offersList);
  const secondaryListHeaderText = `${t('product', 'splitlist-other.title')} 
    (${offersList.secondaryList.length})`;

  return (
    <PricePanel>
      <PriceListHeader offers={offersList} />
      {me?.analyticsAccess?.stores && <MissingShop />}

      {offersList.mainList.length ? (
        <PriceListBody
          isPrimaryList
          sortedPrices={offersList.mainList}
          showMorePrices={showMorePrices}
          numberOfVisiblePrices={listLimits.mainListLimit}
          {...commonListProps}
        />
      ) : (
        <OfferListEmptyMessage />
      )}
      <PriceListFooter
        showProductInformationTexts={showProductInformationTexts}
        sortedPrices={offersList.mainList}
        showMorePrices={showMorePrices}
        numberOfVisiblePrices={listLimits.mainListLimit}
        setShowMorePrices={setShowMorePrices}
      />
      {offersList.secondaryList.length > 0 && (
        <>
          <HeaderWrapper>
            <H3Text>{secondaryListHeaderText}</H3Text>
            <CaptionText scale={FontScale.S} weight={FontWeight.HEAVIER}>
              {t('product', 'splitlist-other.description')}
            </CaptionText>
          </HeaderWrapper>
          <PriceListBody
            isPrimaryList={false}
            sortedPrices={offersList.secondaryList!}
            showMorePrices={showMoreInSecondaryList}
            numberOfVisiblePrices={listLimits.secondaryListLimit}
            {...commonListProps}
          />
          <PriceListFooter
            showProductInformationTexts={showProductInformationTexts}
            sortedPrices={offersList.secondaryList!}
            showMorePrices={showMoreInSecondaryList}
            numberOfVisiblePrices={listLimits.secondaryListLimit}
            setShowMorePrices={setShowMoreInSecondaryList}
          />
        </>
      )}

      {storeStatistics && (
        <PriceInformation storeStatistics={storeStatistics} />
      )}
    </PricePanel>
  );
};
